import { BiPlus } from 'react-icons/bi';
import { FaHeart } from 'react-icons/fa';
import { useEffect, useState, type JSX } from 'react';
import { CollapsibleBottomSheet } from '@/components/layout';
import { CreateNewList } from '@/components/ui';
import {
  addProductToList,
  fetchProductDataById,
  removeProductFromList,
} from '@/services/slaydAPI';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import useToastStore from '@/stores/ToastStore';
import { updatedImageUrl } from '@/utils/formatting';
import styles from './SlaylistSelector.module.css';
import type { SlaylistSelectorProps } from './SlaylistSelector.types';

const SlaylistSelector = ({
  productId,
  onSelect,
  productImage,
}: SlaylistSelectorProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [showCreateNew, setShowCreateNew] = useState<boolean>(false);
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

  const [TheproductImage, setTheProductImage] = useState<string | null>(null);
  const slaylists = useSlaylistSelectorStore((state) => state.slaylists);

  const { showToast } = useToastStore();
  const {
    data,
    isOpen,
    setSelectedSlaylist,
    closeSlaylistSelector,
    getSlaylistIdbyName,
    setProductSlaylist,
  } = useSlaylistSelectorStore();

  // Use productId prop if provided, otherwise fall back to data?.productId
  const currentProductId = productId ?? data?.productId;

  const handleExpandedChange = (newExpandedState: boolean): void => {
    setIsExpanded(newExpandedState);
  };

  const getCurrentWishlistInfo = (): {
    name: string | undefined;
    id: number | undefined;
  } => {
    const globalSelectedSlaylist =
      useSlaylistSelectorStore.getState().selectedSlaylist;
    if (globalSelectedSlaylist) {
      return {
        name: globalSelectedSlaylist,
        id: getSlaylistIdbyName(globalSelectedSlaylist),
      };
    }
    return { name: undefined, id: undefined };
  };

  useEffect(() => {
    // Use productImage prop if provided, otherwise fetch from API
    if (productImage && currentProductId) {
      setTheProductImage(productImage);
    } else if (currentProductId && !productImage) {
      const fetchImage = async (): Promise<void> => {
        try {
          const product = await fetchProductDataById(String(currentProductId));
          const url = updatedImageUrl(product.imageUrl);
          setTheProductImage(url);
        } catch (error) {
          console.error('Failed to fetch product image:', error);
        }
      };
      if (isOpen) {
        fetchImage();
      }
    }
  }, [isOpen, currentProductId, productImage]);

  if (!currentProductId) {
    return <></>;
  }

  const handleSlaylistSelect = async (
    newSlaylistName: string
  ): Promise<void> => {
    if (!currentProductId) {
      console.error('SlaylistSelector has no productId.');
      return;
    }

    const { name: currentWishlistName, id: currentWishlistId } =
      getCurrentWishlistInfo();

    if (currentWishlistName === newSlaylistName) {
      closeSlaylistSelector();
      return;
    }

    // Store previous state for potential rollback
    const previousWishlistName = currentWishlistName;
    const previousWishlistId = currentWishlistId;

    // Optimistically update UI immediately
    const actionText = currentWishlistName ? 'Moved' : 'Added';
    setProductSlaylist(
      currentProductId,
      newSlaylistName,
      getSlaylistIdbyName(newSlaylistName) || -1
    );
    setSelectedSlaylist(newSlaylistName);
    closeSlaylistSelector();
    onSelect?.(newSlaylistName);
    try {
      // Remove from old list if it exists
      if (currentWishlistId && currentWishlistName != 'Saved') {
        await removeProductFromList(currentWishlistId, currentProductId);
      }
      await addProductToList(currentProductId, newSlaylistName);

      showToast(`${actionText} to '${newSlaylistName}'`, {
        text: 'CHANGE',
        action: {
          type: 'change',
          productId: Number(currentProductId),
          productImage: productImage || undefined,
        },
      });
    } catch (error) {
      console.error('Failed to update slaylist:', error);

      // Rollback optimistic updates
      if (previousWishlistName) {
        setProductSlaylist(
          currentProductId,
          previousWishlistName,
          previousWishlistId || -1
        );
        setSelectedSlaylist(previousWishlistName);
      } else {
        // If there was no previous list, remove the product from UI
        setProductSlaylist(currentProductId, '', -1);
        setSelectedSlaylist('');
      }

      // Show error message
      showToast('Failed to update slaylist. Changes reverted.');
    }
  };

  // If the user clicks "Create New", we swap out the entire component view
  if (isOpen && showCreateNew) {
    return (
      <CreateNewList productId={data?.productId} productImage={productImage} />
    );
  }

  if (!isOpen) {
    return <></>;
  }

  return (
    // Use a React Fragment to return two separate, sibling components
    <>
      <CollapsibleBottomSheet
        isExpanded={isExpanded}
        initialHeightPx={335}
        initialHeightPercent={45}
        maxHeight={window.innerHeight - 90}
        onExpandedChange={handleExpandedChange}
        onCollapse={closeSlaylistSelector}
      >
        <div className={styles.scrollableContent}>
          <div className={styles.header}>
            <div className={styles.imageWrapper}>
              {!isImageLoaded && <div className={styles.imagePlaceholder} />}
              {TheproductImage && (
                <img
                  src={TheproductImage}
                  alt="Product"
                  className={styles.productImg}
                  style={{ display: isImageLoaded ? 'block' : 'none' }}
                  onLoad={() => setIsImageLoaded(true)}
                />
              )}
            </div>
            <div className={styles.headerText}>
              <div className={styles.savedTitle}>Saved</div>
              <div className={styles.allProducts}>All Products</div>
            </div>
            <div className={styles.heartIcon}>
              <FaHeart size={23} />
            </div>
          </div>
          <div className={styles.sectionTitle}>
            Select to create a new slaylist
          </div>
          <div className={styles.slaylistOptions}>
            {slaylists.map((slaylist) =>
              slaylist.name === 'Saved' ? (
                <></>
              ) : (
                <button
                  key={slaylist.id}
                  className={styles.slaylistButton}
                  onClick={() => handleSlaylistSelect(slaylist.name)}
                >
                  <span className={styles.plusIcon}>
                    <BiPlus color="#71738A" size={28} />
                  </span>
                  <span className={styles.slaylistName}>{slaylist.name}</span>
                </button>
              )
            )}
          </div>
        </div>
      </CollapsibleBottomSheet>
      <div className={styles.fixedButtonContainer}>
        <button
          className={styles.createNewButton}
          onClick={() => setShowCreateNew(true)}
        >
          <span className={styles.plusIcon}>
            <BiPlus color="#000000" size={28} />
          </span>
          <span className={styles.slaylistName}>Create a new slaylist</span>
        </button>
      </div>
    </>
  );
};

export default SlaylistSelector;
